import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Typography,
  Card,
  Divider,
  Input,
  Popconfirm,
  Modal,
  Form,
  Switch,
  message,
  Tag,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ProjectOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { translationProjectsService, TranslationProject, CreateTranslationProjectRequest, UpdateTranslationProjectRequest } from '../../api/translationProjects';
import { useAuth } from '../../hooks/useAuth';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface ProjectFormData {
  name: string;
  displayName: string;
  description?: string;
  isActive?: boolean;
}

const ProjectsList: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProject, setEditingProject] = useState<TranslationProject | null>(null);
  const [form] = Form.useForm<ProjectFormData>();
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();

  // Fetch projects
  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['translation-projects'],
    queryFn: () => translationProjectsService.getProjects(),
  });

  // Create project mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateTranslationProjectRequest) => 
      translationProjectsService.createProject(data),
    onSuccess: () => {
      message.success('Проект успешно создан');
      queryClient.invalidateQueries({ queryKey: ['translation-projects'] });
      setModalVisible(false);
      form.resetFields();
    },
    onError: (error: Error & {response?: {data?: {message?: string}}}) => {
      message.error('Ошибка при создании проекта: ' + (error.response?.data?.message || error.message));
    },
  });

  // Update project mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTranslationProjectRequest }) =>
      translationProjectsService.updateProject(id, data),
    onSuccess: () => {
      message.success('Проект успешно обновлен');
      queryClient.invalidateQueries({ queryKey: ['translation-projects'] });
      setModalVisible(false);
      setEditingProject(null);
      form.resetFields();
    },
    onError: (error: Error & {response?: {data?: {message?: string}}}) => {
      message.error('Ошибка при обновлении проекта: ' + (error.response?.data?.message || error.message));
    },
  });

  // Delete project mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => translationProjectsService.deleteProject(id),
    onSuccess: () => {
      message.success('Проект успешно удален');
      queryClient.invalidateQueries({ queryKey: ['translation-projects'] });
    },
    onError: (error: Error & {response?: {data?: {message?: string}}}) => {
      message.error('Ошибка при удалении проекта: ' + (error.response?.data?.message || error.message));
    },
  });

  // Filter projects based on search
  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchText.toLowerCase()) ||
    project.displayName.toLowerCase().includes(searchText.toLowerCase()) ||
    (project.description && project.description.toLowerCase().includes(searchText.toLowerCase()))
  );

  const handleCreate = () => {
    setEditingProject(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (project: TranslationProject) => {
    setEditingProject(project);
    form.setFieldsValue({
      name: project.name,
      displayName: project.displayName,
      description: project.description,
      isActive: project.isActive,
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingProject) {
        // Update existing project
        await updateMutation.mutateAsync({
          id: editingProject.id,
          data: {
            displayName: values.displayName,
            description: values.description,
            isActive: values.isActive,
          }
        });
      } else {
        // Create new project
        await createMutation.mutateAsync({
          name: values.name,
          displayName: values.displayName,
          description: values.description,
        });
      }
    } catch (error) {
      // Form validation errors are handled by Ant Design
    }
  };

  const columns = [
    {
      title: 'Название',
      key: 'name',
      render: (record: TranslationProject) => (
        <Space direction="vertical" size={2}>
          <Text strong>{record.displayName}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.name}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => (
        <Text type="secondary">
          {description || '—'}
        </Text>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Активен' : 'Неактивен'}
        </Tag>
      ),
    },
    {
      title: 'Создан',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => (
        <Text type="secondary">
          {new Date(date).toLocaleDateString('ru-RU')}
        </Text>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (record: TranslationProject) => (
        <Space>
          <Tooltip title="Переводы проекта">
            <Button
              type="link"
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => navigate(`/translations/projects/${record.id}`)}
            />
          </Tooltip>
          {hasRole(['ADMIN', 'SUPERADMIN']) && (
            <>
              <Tooltip title="Редактировать">
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
              <Popconfirm
                title="Удалить проект?"
                description="Это действие нельзя отменить. Все переводы проекта будут удалены."
                onConfirm={() => deleteMutation.mutate(record.id)}
                okText="Да"
                cancelText="Нет"
              >
                <Tooltip title="Удалить">
                  <Button
                    type="link"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    loading={deleteMutation.isPending}
                  />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ProjectOutlined />
                Проекты переводов
              </Title>
              <Text type="secondary">
                Управление проектами для организации переводов
              </Text>
            </div>
            {hasRole(['ADMIN', 'SUPERADMIN']) && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
                loading={createMutation.isPending}
              >
                Создать проект
              </Button>
            )}
          </div>

          <Divider />

          {/* Search */}
          <Input
            placeholder="Поиск по названию, описанию..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300, marginBottom: '16px' }}
          />

          {/* Statistics */}
          <div style={{ display: 'flex', gap: '24px', marginBottom: '16px' }}>
            <div>
              <Text type="secondary">Всего проектов: </Text>
              <Text strong>{filteredProjects.length}</Text>
            </div>
            <div>
              <Text type="secondary">Активных: </Text>
              <Text strong>{filteredProjects.filter(p => p.isActive).length}</Text>
            </div>
            <div>
              <Text type="secondary">Неактивных: </Text>
              <Text strong>{filteredProjects.filter(p => !p.isActive).length}</Text>
            </div>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={filteredProjects}
          rowKey="id"
          loading={isLoading || deleteMutation.isPending}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingProject ? 'Редактировать проект' : 'Создать проект'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          setEditingProject(null);
          form.resetFields();
        }}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        okText={editingProject ? 'Обновить' : 'Создать'}
        cancelText="Отмена"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ isActive: true }}
        >
          <Form.Item
            name="name"
            label="Системное название"
            rules={[
              { required: true, message: 'Введите системное название' },
              { pattern: /^[a-z0-9_-]+$/, message: 'Только строчные буквы, цифры, дефисы и подчеркивания' }
            ]}
            extra="Используется в API. Например: oxpos, admin, mobile"
          >
            <Input 
              placeholder="oxpos" 
              disabled={!!editingProject}
            />
          </Form.Item>

          <Form.Item
            name="displayName"
            label="Отображаемое название"
            rules={[{ required: true, message: 'Введите отображаемое название' }]}
          >
            <Input placeholder="OX POS System" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Описание"
          >
            <TextArea 
              rows={3}
              placeholder="Переводы для системы торговых точек OX POS"
            />
          </Form.Item>

          {editingProject && (
            <Form.Item
              name="isActive"
              label="Статус"
              valuePropName="checked"
            >
              <Switch checkedChildren="Активен" unCheckedChildren="Неактивен" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default ProjectsList;
